import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';

// EVERI-3323: lets a verification panel render content into the page-level
// navigation row (next to Previous/Submit), which is otherwise a sibling
// subtree it can't reach. Only the multi-doc upload panel sets a slot today;
// for every other verification type the slot stays null and the nav is
// unchanged.
//
// EVERI-3325: `suppressSubmit` additionally lets a panel hide the page-level
// Submit button. The multi-doc panel sets this on-flag because the flow has
// no Save/Submit button — submission is finalized from the modal confirms
// instead (see EmploymentMultidocPanel). Defaults to false so every other
// flow keeps its Submit button.
// EVERI-3323: state and setters live in separate contexts. The state context
// changes whenever `navSlot`/`suppressSubmit` change (each `setNavSlot` stores a
// fresh React element); the setters context is stable for the provider's
// lifetime (useState setters never change). Splitting them means a consumer that
// only needs the setters — e.g. the multi-doc panel that *produces* the slot —
// doesn't re-render every time the slot content changes.
interface NavSlotState {
  navSlot: ReactNode;
  suppressSubmit: boolean;
  disablePrevious: boolean;
}

interface NavSlotSetters {
  setNavSlot: (node: ReactNode) => void;
  setSuppressSubmit: (value: boolean) => void;
  setDisablePrevious: (value: boolean) => void;
}

const NavSlotStateContext = createContext<NavSlotState>({
  navSlot: null,
  suppressSubmit: false,
  disablePrevious: false,
});

const NavSlotSettersContext = createContext<NavSlotSetters>({
  setNavSlot: () => {},
  setSuppressSubmit: () => {},
  setDisablePrevious: () => {},
});

export const NavSlotProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [navSlot, setNavSlot] = useState<ReactNode>(null);
  const [suppressSubmit, setSuppressSubmit] = useState<boolean>(false);
  // EVERI-4005: a panel can disable Previous while an inline edit (e.g. candidate context) is open, so navigating away can't silently drop it.
  const [disablePrevious, setDisablePrevious] = useState<boolean>(false);
  const state = useMemo(
    () => ({ navSlot, suppressSubmit, disablePrevious }),
    [navSlot, suppressSubmit, disablePrevious],
  );
  // Setters are referentially stable, so this object is created once and never
  // changes — setter-only consumers never re-render from slot/state updates.
  const setters = useMemo(
    () => ({ setNavSlot, setSuppressSubmit, setDisablePrevious }),
    [],
  );
  return (
    <NavSlotSettersContext.Provider value={setters}>
      <NavSlotStateContext.Provider value={state}>
        {children}
      </NavSlotStateContext.Provider>
    </NavSlotSettersContext.Provider>
  );
};

export const NavSlotOutlet: React.FC = () => {
  const { navSlot } = useContext(NavSlotStateContext);
  return <>{navSlot}</>;
};

// Read-only state (navSlot, suppressSubmit) — re-renders on state change.
export const useNavSlotState = (): NavSlotState =>
  useContext(NavSlotStateContext);

// Stable setters — never trigger a re-render on slot/state change.
export const useNavSlotSetters = (): NavSlotSetters =>
  useContext(NavSlotSettersContext);
